import { Typography, makeStyles } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import {
  formatUptime,
  PROBE_HOW_TO,
  STATUS_COLOUR,
  STATUS_LABEL,
} from '../catalog/component/serviceStatus';
import {
  formatDay,
  measuredSince,
  summariseDays,
  type OverviewGroup,
  type OverviewService,
} from './overview';
import { StatusCard } from './StatusCard';

const useStyles = makeStyles(theme => ({
  group: {
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid var(--ui-border)',
  },
  groupName: {
    margin: theme.spacing(0, 0, 1),
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--ui-text-muted)',
  },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(7rem, 11rem) minmax(0, 1fr) 5.5rem 8.5rem',
    gridTemplateAreas: '"name bars uptime state"',
    alignItems: 'center',
    columnGap: theme.spacing(1.5),
    rowGap: theme.spacing(0.5),
    padding: theme.spacing(0.625, 0),
    minWidth: 0,
    // On a phone: name and state above, the bars across the line beneath.
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gridTemplateAreas: '"name state" "bars bars" "uptime uptime"',
      padding: theme.spacing(1, 0),
    },
  },
  name: {
    gridArea: 'name',
    fontSize: '0.875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  internal: {
    marginLeft: theme.spacing(0.75),
    fontSize: '0.6875rem',
    color: 'var(--ui-text-muted)',
  },
  bars: {
    gridArea: 'bars',
    display: 'flex',
    gap: 1,
    height: 22,
    minWidth: 0,
  },
  bar: {
    flex: '1 1 0',
    minWidth: 1,
    borderRadius: 1,
  },
  uptime: {
    gridArea: 'uptime',
    fontSize: '0.8125rem',
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    color: 'var(--ui-text-muted)',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: { textAlign: 'left' },
  },
  // Beneath the figure on a wide screen, beside it on a phone.
  since: {
    display: 'block',
    fontSize: '0.6875rem',
    [theme.breakpoints.down('xs')]: {
      display: 'inline',
      '&::before': { content: '" · "' },
    },
  },
  state: {
    gridArea: 'state',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
  },
  dot: { width: 8, height: 8, borderRadius: '50%', flex: '0 0 auto' },
  foot: {
    padding: theme.spacing(1.25, 2),
    fontSize: '0.75rem',
    color: 'var(--ui-text-muted)',
  },
  empty: { padding: theme.spacing(2), color: 'var(--ui-text-muted)' },
}));

const ServiceRow = ({
  service,
  windowDays,
}: {
  service: OverviewService;
  windowDays: number;
}) => {
  const classes = useStyles();
  const since = measuredSince(service.days);
  return (
    <li className={classes.row} aria-label={service.name}>
      <span className={classes.name}>
        {service.name}
        {service.visibility === 'internal' && (
          <span className={classes.internal}>internal</span>
        )}
      </span>
      <div
        className={classes.bars}
        role="img"
        aria-label={`${service.name}, last ${windowDays} days: ${summariseDays(
          service.days,
        )}`}
      >
        {service.days.map(day => (
          <span
            key={day.date}
            className={classes.bar}
            style={{ background: STATUS_COLOUR[day.status] }}
            title={`${formatDay(day.date)} · ${
              day.uptimePct === null ? 'No data' : formatUptime(day.uptimePct)
            }`}
          />
        ))}
      </div>
      <span className={classes.uptime}>
        {service.uptimePct === null
          ? 'No data'
          : formatUptime(service.uptimePct)}
        {since && service.uptimePct !== null && (
          <span className={classes.since}>since {formatDay(since)}</span>
        )}
      </span>
      <span className={classes.state}>
        <span
          className={classes.dot}
          style={{ background: STATUS_COLOUR[service.status] }}
          aria-hidden
        />
        {STATUS_LABEL[service.status]}
      </span>
    </li>
  );
};

/**
 * Every service of the view, by its group, with a bar per day and its uptime
 * over the window (specs/status-page.md AC-1). A service nothing measured
 * reads "No data" and shows no figure; its bars stay grey.
 */
export const ComponentsCard = ({
  groups,
  windowDays,
  customerFacing,
}: {
  groups: OverviewGroup[];
  windowDays: number;
  customerFacing: boolean;
}) => {
  const classes = useStyles();
  return (
    <StatusCard
      id="status-components"
      title="Components"
      aside={`${windowDays} days`}
    >
      {groups.length === 0 ? (
        <Typography className={classes.empty}>
          The status page lists no{' '}
          {customerFacing ? 'customer-facing service' : 'service'} yet.
        </Typography>
      ) : (
        groups.map(group => (
          <div key={group.slug} className={classes.group}>
            <h3 id={`status-group-${group.slug}`} className={classes.groupName}>
              {group.name}
            </h3>
            <ul
              className={classes.list}
              aria-labelledby={`status-group-${group.slug}`}
            >
              {group.services.map(service => (
                <ServiceRow
                  key={service.slug}
                  service={service}
                  windowDays={windowDays}
                />
              ))}
            </ul>
          </div>
        ))
      )}
      <Typography className={classes.foot}>
        Probes are declared in each repository's .rxmeta/manifest.json and
        synced from GitHub every hour —{' '}
        <Link to={PROBE_HOW_TO}>how to add one</Link>.
      </Typography>
    </StatusCard>
  );
};
